"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

// useLayoutEffect on the client, useEffect on the server (avoids the SSR warning).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Publishes the enclosing <header>'s height as `--storefront-header-h` so
 * sticky page furniture (the product filter rail) can sit below it. Render it
 * inside every storefront header. The header has no height class — it grows
 * with the tenant's logo, announcement bar and breakpoints — so a hardcoded
 * offset tucked the filters under taller headers. A header that isn't
 * sticky or fixed publishes 0: nothing stays on screen to clear.
 */
export function HeaderHeightPublisher() {
  const ref = useRef<HTMLSpanElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const header = ref.current?.closest("header");
    if (!header) return;
    const publish = () => {
      const { position } = getComputedStyle(header);
      const pinned = position === "sticky" || position === "fixed";
      document.documentElement.style.setProperty(
        "--storefront-header-h",
        `${pinned ? Math.round(header.getBoundingClientRect().height) : 0}px`,
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return <span ref={ref} hidden />;
}
